// Owner: S1, views-grid (wave 2) — saved views, teams, the board card order (views-layouts) and export
// on the contracts of `@dataroom/contracts/views` and `/view-layouts` (`api.call(listViewsContract, …)` works the same; this group keeps the
// short names its callers use).
//   import { viewsApi } from '@dataroom/api-client/views'; const views = viewsApi(api.http);
import {
  createViewContract,
  deleteViewContract,
  duplicateViewContract,
  favoriteViewContract,
  getViewContract,
  listViewsContract,
  reorderViewsContract,
  updateViewContract,
  viewTeamsContract,
} from '@dataroom/contracts/views';
import { moveCardRankContract } from '@dataroom/contracts/view-layouts';
import type {
  BulkActionInput,
  BulkActionResult,
  CreateViewInput,
  ExportRequest,
  UpdateViewInput,
} from '@dataroom/core/views';
import { ApiError, type Http, type HttpOptions, seg } from './http';
import { createCall } from './typed';

export type CsvDownload = { filename: string; blob: Blob; rows: number };

export const viewsApi = (http: Http, options: Pick<HttpOptions, 'baseUrl' | 'fetch'> = {}) => {
  const call = createCall(http);
  return {
    list: (objectKey: string) => call(listViewsContract, { params: { key: objectKey } }),
    get: (objectKey: string, view: string) => call(getViewContract, { params: { key: objectKey, view } }),
    create: (objectKey: string, input: CreateViewInput) =>
      call(createViewContract, { params: { key: objectKey }, body: input }),
    update: (objectKey: string, view: string, input: UpdateViewInput) =>
      call(updateViewContract, { params: { key: objectKey, view }, body: input }),
    delete: (objectKey: string, view: string) =>
      call(deleteViewContract, { params: { key: objectKey, view } }),
    duplicate: (objectKey: string, view: string, name?: string) =>
      call(duplicateViewContract, { params: { key: objectKey, view }, body: name ? { name } : {} }),
    reorder: (objectKey: string, ids: string[]) =>
      call(reorderViewsContract, { params: { key: objectKey }, body: { ids } }),
    favorite: (objectKey: string, view: string, favorite: boolean) =>
      call(favoriteViewContract, { params: { key: objectKey, view }, body: { favorite } }),
    teams: () => call(viewTeamsContract),
    /** Place a card in a board view's manual order (`order` = the target column's ids, top to bottom). */
    moveRank: (objectKey: string, view: string, recordId: string, order: string[]) =>
      call(moveCardRankContract, { params: { key: objectKey, view }, body: { recordId, order } }),
    /** Legacy bulk bar body (views-filters-bulk moves the bar to the typed bulk API). */
    bulk: (objectKey: string, input: BulkActionInput) =>
      http.post<BulkActionResult>(seg`/objects/${objectKey}/bulk`, input),
    /** CSV export (binary response, so it bypasses the JSON helper). */
    exportCsv: async (objectKey: string, input: ExportRequest): Promise<CsvDownload> => {
      const doFetch = options.fetch ?? ((...args: Parameters<typeof fetch>) => globalThis.fetch(...args));
      const res = await doFetch(`${options.baseUrl ?? '/api/v1'}${seg`/objects/${objectKey}/export`}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', 'x-dataroom-client': 'web', accept: 'text/csv' },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: { code?: string; message?: string };
        } | null;
        throw new ApiError(
          res.status,
          (body?.error?.code as ApiError['code'] | undefined) ?? 'internal',
          body?.error?.message ?? res.statusText,
        );
      }
      const disposition = res.headers.get('content-disposition') ?? '';
      const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `${objectKey}.csv`;
      return { filename, blob: await res.blob(), rows: Number(res.headers.get('x-export-rows') ?? 0) };
    },
  };
};

export type ViewsApi = ReturnType<typeof viewsApi>;
